import { ContractTemplateFieldName } from '@/core/collections/contracts-api/contract-template-field-names/contract-template-field-name.interface';
import { ContractTemplateCalculatedValue } from '@/core/collections/contracts-api/customers/contract-templates/contract-template.interface';
import { FormType } from '@/core/models/form-type.enum';
import { normalizeYupErrorPath } from '@nxtpeople/nxt-styles';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';
import { ValidationError } from 'yup';

export type ContractTemplateCalculatedValuesFormData = {
   field: ContractTemplateField;
   salary_type_field: ContractTemplateField;
   calculated_values: Array<ContractTemplateCalculatedValue>;
   salary_type_calculated_values: Array<ContractTemplateCalculatedValue>;
};

export function useContractTemplateCalculatedValuesSchema(
   fieldName: ContractTemplateFieldName,
   salaryTypeFieldName?: ContractTemplateFieldName,
) {
   const { t } = useI18n();
   const requiredMessage = t('generic.feedback.required');

   const CALCULATED_VALUES_SCHEMA = yup
      .object({
         calculated_values: yup.array().min(1).required(),
         salary_type_calculated_values: yup.array().optional(),
      })
      .test('calculated-values-rows', '', function validateCalculatedValues(formData: ContractTemplateCalculatedValuesFormData) {
         const innerErrors: ValidationError[] = [];
         const fieldKey = fieldName.key;
         const salaryTypeKey = salaryTypeFieldName?.key;

         formData.calculated_values?.forEach((calculatedValue, rowIndex) => {
            if (calculatedValue.value === null || calculatedValue.value === undefined || calculatedValue.value === '') {
               innerErrors.push(new ValidationError(requiredMessage, calculatedValue.value, `${fieldKey}_${rowIndex}_value`));
            }

            if (salaryTypeKey === 'salary_type_id') {
               const salaryTypeValue = formData.salary_type_calculated_values?.[rowIndex]?.value;
               if (salaryTypeValue === null || salaryTypeValue === undefined || salaryTypeValue === '') {
                  innerErrors.push(new ValidationError(requiredMessage, salaryTypeValue, `${salaryTypeKey}_${rowIndex}_value`));
               }
            }

            calculatedValue.conditions?.forEach((condition, conditionIndex) => {
               if (!condition?.calculated_field_condition_type_id) {
                  innerErrors.push(
                     new ValidationError(
                        requiredMessage,
                        condition?.calculated_field_condition_type_id,
                        `calculated_value_${rowIndex}_condition_type_${conditionIndex}`,
                     ),
                  );
               }

               const formType = condition?.calculated_field_condition_type?.contract_template_field_type?.form_type;
               if (formType === FormType.NumberRange) {
                  if (!condition?.min_value) {
                     innerErrors.push(
                        new ValidationError(
                           requiredMessage,
                           condition?.min_value,
                           `calculated_value_${rowIndex}_value_min_${conditionIndex}`,
                        ),
                     );
                  }
                  if (!condition?.max_value) {
                     innerErrors.push(
                        new ValidationError(
                           requiredMessage,
                           condition?.max_value,
                           `calculated_value_${rowIndex}_value_max_${conditionIndex}`,
                        ),
                     );
                  }
               } else if (condition?.calculated_field_condition_type_id) {
                  if (condition.value === null || condition.value === undefined || condition.value === '') {
                     innerErrors.push(
                        new ValidationError(
                           requiredMessage,
                           condition.value,
                           `calculated_value_${rowIndex}_condition_type_${conditionIndex}`,
                        ),
                     );
                  }
               }
            });
         });

         if (innerErrors.length) {
            throw new ValidationError(innerErrors);
         }

         return true;
      });

   return { CALCULATED_VALUES_SCHEMA };
}

export function mapContractTemplateCalculatedValuesFormErrors(errors: Record<string, string>) {
   const mapped: Record<string, string> = {};

   for (const [path, message] of Object.entries(errors)) {
      const normalizedPath = normalizeYupErrorPath(path);
      mapped[normalizedPath] = message;
      mapped[path] = message;
   }

   return mapped;
}

export function cloneCalculatedValuesFormData(
   field: ContractTemplateField,
   salaryTypeField: ContractTemplateField,
): ContractTemplateCalculatedValuesFormData {
   return {
      field: JSON.parse(JSON.stringify(field)),
      salary_type_field: JSON.parse(JSON.stringify(salaryTypeField)),
      calculated_values: JSON.parse(JSON.stringify(field.calculated_values || [])),
      salary_type_calculated_values: JSON.parse(JSON.stringify(salaryTypeField.calculated_values || [])),
   };
}
